import * as auth from '../auth.js';
import * as i18n from '../i18n.js';
import { getState } from '../store.js';
import * as router from '../router.js';
import { setOutlet, rerender } from '../router.js';
import { userMenu } from './user-menu.js';
import { bottomNav } from './bottom-nav.js';
import { sidebar } from './sidebar.js';
import { itemForPath } from './catalog-items.js';
import { reminderBell } from './reminder-bell.js';
import { timerIndicator } from './timer-indicator.js';
import { mountAuthedShell } from '../shell.js';
import { tipStrip } from './tip-strip.js';
import { brandLockup } from './brand-lockup.js';
import { currentRole, hasCapability } from '../capabilities.js';
import { iconUsers, iconTheme } from './brand-icons.js';

// Shared page content column — <main> and the tip strip constrain their
// content to the same width + horizontal padding so they stay aligned by
// construction (change it here, both surfaces follow). Wider than the old
// max-w-5xl because the sidebar now takes the left edge and the desktop job is
// planning and maintenance, which wants density rather than margin.
const CONTENT_COLUMN = 'max-w-[1100px] w-full mx-auto px-4 lg:px-6';

// Routes that are not registry entries but still deserve a header title.
const EXTRA_TITLE_KEYS = {
  '/profile': 'nav.profile',
  '/reminders': 'reminders.title',
  '/shopping/put-away': 'shopping.put_away_mode_title',
};

// Chrome components own live intervals and document-level listeners that
// discarding their DOM node does NOT stop. mountLayout() rebuilds the shell
// from scratch on every call (boot, login, logout, locale switch, profile
// save), so every such component is tracked here and torn down before the new
// shell is built. Before this list existed only the timer indicator was
// released; the bottom nav's hashchange listener, and three document listeners
// per open dropdown, leaked on every remount.
/** @type {Array<{ destroy: () => void }>} */
let activeChrome = [];

/** @type {HTMLElement|null} */
let summaryEl = null;
/** @type {HTMLElement|null} */
let pageTitleEl = null;
/** @type {HTMLElement|null} */
let announcerEl = null;

/**
 * Write the header's right-aligned summary line (e.g. "54 articles · 3 due
 * soon"). Cleared automatically on every route change, so a page sets it in
 * mount() and never has to clean up.
 *
 * @param {string} text
 */
export function setHeaderSummary(text) {
  if (summaryEl) summaryEl.textContent = text || '';
}

/**
 * Chrome-side page title and screen-reader route announcement. Called by the
 * router after each render; pages do not call this.
 *
 * @param {string} path
 */
export function setHeaderRoute(path) {
  const item = itemForPath(path);
  // EXTRA_TITLE_KEYS wins over an item found by prefix match, not only over a
  // miss: `/shopping/put-away` prefix-matches the `/shopping` registry entry
  // (itemForPath's longest-prefix rule), which would otherwise always shadow
  // its own EXTRA_TITLE_KEYS entry.
  const key = (Object.prototype.hasOwnProperty.call(EXTRA_TITLE_KEYS, path) ? EXTRA_TITLE_KEYS[path] : undefined)
    || (item ? item.titleKey : undefined);
  const label = key ? i18n.t(key) : '';
  if (pageTitleEl) pageTitleEl.textContent = label;
  if (summaryEl) summaryEl.textContent = '';
  if (announcerEl) announcerEl.textContent = label;
}

function localeSelect(rootEl) {
  const sel = document.createElement('select');
  sel.dataset.role = 'locale-select';
  sel.className = 'input !w-auto !min-h-0 py-1 text-sm';
  sel.setAttribute('aria-label', i18n.t('nav.language'));
  for (const loc of getState().locales) {
    const o = document.createElement('option');
    o.value = loc;
    o.textContent = loc.toUpperCase();
    if (loc === getState().locale) o.selected = true;
    sel.append(o);
  }
  sel.addEventListener('change', async (e) => {
    const next = e.target.value;
    try {
      await i18n.loadLocale(next);
      localStorage.setItem('lardr.locale', next);
      // Re-mount the shell to retranslate chrome, then re-render the current
      // route so the page content is translated and the outlet is populated.
      mountLayout(rootEl);
      rerender();
    } catch {}
  });
  return sel;
}

export function mountLayout(rootEl) {
  for (const c of activeChrome) {
    try { c.destroy(); } catch {}
  }
  activeChrome = [];
  summaryEl = pageTitleEl = announcerEl = null;

  // Clear root safely
  while (rootEl.firstChild) rootEl.removeChild(rootEl.firstChild);

  const authed = auth.isAuthenticated();

  const shell = document.createElement('div');
  shell.className = authed
    ? 'min-h-screen lg:grid lg:grid-cols-[244px_1fr]'
    : 'min-h-screen flex flex-col';

  // Skip link — first focusable element on the page. Without it, a keyboard
  // user tabs through the whole sidebar on every navigation.
  if (authed) {
    const skip = document.createElement('a');
    skip.href = '#page-outlet';
    skip.dataset.role = 'skip-link';
    skip.textContent = i18n.t('nav.skip_to_content');
    skip.className = [
      'sr-only focus:not-sr-only',
      'focus:absolute focus:z-50 focus:top-2 focus:left-2',
      'focus:px-4 focus:py-2 focus:rounded-xl',
      'focus:bg-surface-light focus:text-ink focus:shadow-card',
      'focus:outline-none focus:ring-2 focus:ring-primary',
    ].join(' ');
    skip.addEventListener('click', (ev) => {
      ev.preventDefault();
      const outlet = document.getElementById('page-outlet');
      if (outlet) {
        outlet.setAttribute('tabindex', '-1');
        outlet.focus();
      }
    });
    shell.append(skip);

    const side = sidebar({
      role: currentRole(),
      currentPath: (window.location.hash || '#/').slice(1) || '/',
    });
    // Sticky rather than fixed: it stays put while the content scrolls but
    // keeps a normal-flow grid column, so it needs no z-index at all.
    side.el.classList.add('lg:sticky', 'lg:top-0', 'lg:h-screen');
    shell.append(side.el);
    activeChrome.push(side);
  }

  const contentCol = document.createElement('div');
  contentCol.className = 'flex flex-col min-w-0 min-h-screen';

  // Header is a banner landmark, not a nav: after the tab row moved into the
  // sidebar it contains no navigation links.
  const header = document.createElement('header');
  header.className = 'app-header sticky top-0';

  // Brand shows below `lg`, where there is no sidebar to carry it. On the
  // login screen it is the only chrome there is, so it shows at every width.
  const lockup = brandLockup({
    emblemClasses: 'h-10 w-10 sm:h-11 sm:w-11',
    wordClasses: 'font-bold text-2xl tracking-tight',
    tagline: !authed,
  });
  if (authed) lockup.classList.add('lg:hidden');
  header.append(lockup);

  if (authed) {
    pageTitleEl = document.createElement('span');
    pageTitleEl.dataset.role = 'header-page-title';
    pageTitleEl.className = 'hidden lg:block text-lg font-semibold tracking-tight';
    header.append(pageTitleEl);
  }

  const spacer = document.createElement('div');
  spacer.className = 'flex-1 min-w-0';
  header.append(spacer);

  if (authed) {
    summaryEl = document.createElement('span');
    summaryEl.dataset.role = 'header-summary';
    summaryEl.className = 'hidden text-xs text-ink-muted lg:block';
    header.append(summaryEl);
  }

  if (!authed) {
    // The login screen keeps a locale switcher of its own — there is no user
    // menu to hide it in yet.
    const sel = localeSelect(rootEl);
    sel.className = 'bg-surface border border-border text-ink text-sm rounded-xl px-2 py-1 pr-7 w-auto focus:outline-none focus:ring-2 focus:ring-primary-500/50 cursor-pointer dark:bg-surface-card-dark dark:border-border-dark dark:text-ink-on-dark [&>option]:bg-surface-light [&>option]:text-ink dark:[&>option]:bg-surface-card-dark dark:[&>option]:text-ink-on-dark';
    header.append(sel);
  }

  const themeBtn = document.createElement('button');
  themeBtn.type = 'button';
  themeBtn.className = 'btn-ghost text-sm';
  themeBtn.dataset.role = 'theme-toggle';
  themeBtn.append(iconTheme());
  themeBtn.setAttribute('aria-label', i18n.t('nav.toggle_theme_aria'));
  themeBtn.title = i18n.t('nav.toggle_theme_aria');
  themeBtn.addEventListener('click', () => {
    const root = document.documentElement;
    root.classList.toggle('dark');
    localStorage.setItem('lardr.theme', root.classList.contains('dark') ? 'dark' : 'light');
  });
  header.append(themeBtn);

  if (authed) {
    const bell = reminderBell();
    header.append(bell.el);
    if (typeof bell.destroy === 'function') activeChrome.push(bell);

    const indicator = timerIndicator();
    header.append(indicator.el);
    activeChrome.push(indicator);

    const cu = (getState().household && getState().household.current_user) || {};
    const fallbackName = cu.email ? cu.email.split('@')[0] : '';
    // Same capability the registry uses for the /users row, so the menu entry
    // and the sidebar row can never disagree about who may manage members.
    const adminExtras = hasCapability('manage_household')
      ? [{ href: '#/users', iconFn: iconUsers, labelKey: 'nav.users', hiddenAtBreakpoint: 'sm' }]
      : [];
    const menu = userMenu({
      name: cu.name || fallbackName,
      icon: cu.icon || '👤',
      email: cu.email || '',
      extraItems: adminExtras,
      localeControl: localeSelect(rootEl),
      onLogout: async () => {
        await auth.logout();
        await mountAuthedShell();
        router.go('/login');
      },
    });
    header.append(menu.el);
    if (typeof menu.destroy === 'function') activeChrome.push(menu);
  }

  contentCol.append(header);

  // Food-tip strip — sibling of <main> (not inside it) so pb-nav-safe on
  // <main> is unaffected. Both move into the content column together, which is
  // what keeps them siblings.
  if (authed) {
    contentCol.append(tipStrip({ columnClasses: CONTENT_COLUMN }).el);
  }

  const main = document.createElement('main');
  main.id = 'page-outlet';
  // pb-nav-safe on phones/tablets clears the fixed bottom-nav (~56px + safe-area).
  // At `lg` and above, the bottom-nav is hidden so we restore the desktop padding.
  main.className = `flex-1 ${CONTENT_COLUMN} py-4 pb-nav-safe lg:py-6`;
  contentCol.append(main);

  if (authed) {
    announcerEl = document.createElement('div');
    announcerEl.dataset.role = 'route-announcer';
    announcerEl.setAttribute('aria-live', 'polite');
    announcerEl.setAttribute('aria-atomic', 'true');
    announcerEl.className = 'sr-only';
    contentCol.append(announcerEl);
  }

  shell.append(contentCol);

  // Bottom-nav — visible only below `lg` via its own `lg:hidden`. Sibling of
  // the content column so its `fixed` positioning stays viewport-relative.
  if (authed) {
    const bn = bottomNav({
      currentPath: (window.location.hash || '#/').slice(1) || '/',
    });
    shell.append(bn.el);
    activeChrome.push(bn);
  }

  rootEl.append(shell);
  setOutlet(main);
}
